import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { PaymentBadge } from "../lib/paymentMethods";
import { RowMenu } from "./RowMenu";
import { toast } from "sonner";
import { Printer } from "lucide-react";

const money = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(v || 0);
const today = () => new Date().toISOString().slice(0, 10);

// Factures réglées (totalement ou partiellement) encaissées le jour choisi — visibles directement en caisse
export const PaidInvoices = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/cash/paid-invoices?date=${date}`).then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement des factures réglées")), [date]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="paid-invoices">
      <div className="flex flex-wrap items-end gap-2 no-print">
        <div><Label className="overline block mb-1">Journée</Label><Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="rounded-sm h-9 bg-white" data-testid="paid-invoices-date" /></div>
        <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setDate(today())} data-testid="paid-invoices-today">Aujourd'hui</Button>
        <Button variant="outline" size="sm" className="rounded-sm h-9 ml-auto" onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
      </div>
      <Card className="table-container">
        <Table className="data-table">
          <TableHeader className="table-header"><TableRow><TableHead>N° facture</TableHead><TableHead>Heure</TableHead><TableHead>Client</TableHead><TableHead>Mode(s)</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Encaissé ce jour</TableHead><TableHead className="text-right">Reste dû</TableHead><TableHead>État</TableHead></TableRow></TableHeader>
          <TableBody>
            {!data ? <TableRow><TableCell colSpan={8} className="text-center py-10"><div className="spinner mx-auto" /></TableCell></TableRow>
              : data.invoices.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400" data-testid="paid-invoices-empty">Aucune facture réglée ce jour</TableCell></TableRow>
              : data.invoices.map((s) => (
                <RowMenu key={s.id} title={`Facture ${s.invoice_number}`} copyText={s.invoice_number} items={[
                  { label: "Ouvrir la facture", onClick: () => navigate(`/invoice/${s.id}`) },
                  s.customer_id && { label: "Relevé du client", onClick: () => navigate(`/accounts/customer/${s.customer_id}`) },
                ]}>
                <TableRow onDoubleClick={() => navigate(`/invoice/${s.id}`)} className="table-row cursor-pointer" data-testid={`paid-invoice-${s.invoice_number}`}>
                  <TableCell className="font-mono font-medium">{s.invoice_number}</TableCell>
                  <TableCell className="text-sm text-slate-600">{new Date(s.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</TableCell>
                  <TableCell>{s.customer_name}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{s.methods.map((m) => <PaymentBadge key={m} method={m} />)}</div></TableCell>
                  <TableCell className="text-right font-mono">{money(s.total)}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-green-700">{money(s.paid_today)}</TableCell>
                  <TableCell className="text-right font-mono">{s.balance_due > 0 ? <span className="text-orange-600">{money(s.balance_due)}</span> : "—"}</TableCell>
                  <TableCell>{s.state === "paid" ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">Payée</span> : <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs">Partielle</span>}</TableCell>
                </TableRow>
                </RowMenu>
              ))}
          </TableBody>
          {data && data.invoices.length > 0 && (
            <tfoot><tr className="bg-slate-900 text-white"><td colSpan={5} className="px-4 py-2 font-bold text-right">TOTAL ENCAISSÉ · {data.invoices.length} facture{data.invoices.length > 1 ? "s" : ""}</td><td className="px-4 py-2 text-right font-mono font-bold" data-testid="paid-invoices-total">{money(data.total_paid_today)}</td><td colSpan={2}></td></tr></tfoot>
          )}
        </Table>
      </Card>
    </div>
  );
};
